import React, { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const LONG_FIELDS = new Set(["hero_subtitle", "flavors_note", "bundles_subtitle", "pickup_text", "pickup_card1_text", "pickup_card2_text", "contact_subtitle"]);

const GROUPS = [
  { title: "Header & Footer", keys: { brand_name: "Brand name", footer_tagline: "Footer tagline" } },
  { title: "Hero (top of page)", keys: { hero_badge: "Badge text", hero_title_1: "Headline line 1", hero_title_2: "Headline line 2 (brown)", hero_subtitle: "Subtitle", hero_cta_primary: "Primary button", hero_cta_secondary: "Secondary button" } },
  { title: "Flavors Section", keys: { flavors_eyebrow: "Eyebrow", flavors_title: "Title", flavors_note: "Side note" } },
  { title: "Bundles Section", keys: { bundles_eyebrow: "Eyebrow", bundles_title: "Title", bundles_subtitle: "Subtitle" } },
  { title: "Other Products Section", keys: { products_eyebrow: "Eyebrow", products_title: "Title" } },
  { title: "Pickup Section", keys: { pickup_title: "Title", pickup_text: "Description", pickup_card1_title: "Card 1 title", pickup_card1_text: "Card 1 text", pickup_card2_title: "Card 2 title", pickup_card2_text: "Card 2 text" } },
  { title: "Contact Section", keys: { contact_eyebrow: "Eyebrow", contact_title: "Title", contact_subtitle: "Subtitle" } },
];

const PRICE_LABELS = { single: "Single cookie ($)", bundle3: "3-Pack ($)", bundle6: "6-Pack ($)", bundle12: "12-Pack ($)" };
const CONTACT_LABELS = { phone: "Phone", email: "Email", tiktok: "TikTok handle", instagram: "Instagram handle" };

export const AdminContent = ({ token }) => {
  const [content, setContent] = useState({});
  const [prices, setPrices] = useState({});
  const [contact, setContact] = useState({});
  const [busy, setBusy] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    api.get("/admin/config", authHeader).then((res) => {
      setContent(res.data.content || {});
      setPrices(res.data.prices || {});
      setContact(res.data.contact || {});
    }).catch(() => toast.error("Could not load content."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveContent = async () => {
    setBusy(true);
    try { await api.put("/admin/content", { content }, authHeader); toast.success("Text saved!"); }
    catch { toast.error("Save failed."); } finally { setBusy(false); }
  };
  const savePrices = async () => {
    setBusy(true);
    try {
      const clean = {};
      Object.keys(PRICE_LABELS).forEach((k) => { clean[k] = parseFloat(prices[k]); });
      if (Object.values(clean).some((v) => isNaN(v) || v < 0)) return toast.error("Enter valid prices.");
      await api.put("/admin/prices", { prices: clean }, authHeader);
      toast.success("Prices saved!");
    } catch { toast.error("Save failed."); } finally { setBusy(false); }
  };
  const saveContact = async () => {
    setBusy(true);
    try { await api.put("/admin/contact", { contact }, authHeader); toast.success("Contact info saved!"); }
    catch { toast.error("Save failed."); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      {/* Prices */}
      <section className="rounded-2xl border border-ashy/30 bg-white p-6">
        <h3 className="font-heading text-2xl font-bold text-ink">Prices</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Object.keys(PRICE_LABELS).map((k) => (
            <div key={k}>
              <Label>{PRICE_LABELS[k]}</Label>
              <Input data-testid={`price-${k}`} type="number" step="0.01" value={prices[k] ?? ""} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })} className="mt-1.5" />
            </div>
          ))}
        </div>
        <Button data-testid="save-prices-button" onClick={savePrices} disabled={busy} className="mt-5 rounded-full bg-cocoa text-white hover:bg-cocoadark"><Save className="mr-1.5 h-4 w-4" /> Save prices</Button>
      </section>

      {/* Contact */}
      <section className="rounded-2xl border border-ashy/30 bg-white p-6">
        <h3 className="font-heading text-2xl font-bold text-ink">Contact Info</h3>
        <p className="text-sm text-ashydark">Shown in the Contact section. Enter handles without the @.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {Object.keys(CONTACT_LABELS).map((k) => (
            <div key={k}>
              <Label>{CONTACT_LABELS[k]}</Label>
              <Input data-testid={`contact-${k}`} value={contact[k] ?? ""} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} className="mt-1.5" />
            </div>
          ))}
        </div>
        <Button data-testid="save-contact-button" onClick={saveContact} disabled={busy} className="mt-5 rounded-full bg-cocoa text-white hover:bg-cocoadark"><Save className="mr-1.5 h-4 w-4" /> Save contact info</Button>
      </section>

      {/* Text content */}
      <section className="rounded-2xl border border-ashy/30 bg-white p-6">
        <h3 className="font-heading text-2xl font-bold text-ink">Website Text</h3>
        <p className="text-sm text-ashydark">Edit any words on your homepage.</p>
        <div className="mt-4 space-y-6">
          {GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-cocoa">{group.title}</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                {Object.entries(group.keys).map(([key, label]) => (
                  <div key={key} className={LONG_FIELDS.has(key) ? "sm:col-span-2" : ""}>
                    <Label>{label}</Label>
                    {LONG_FIELDS.has(key) ? (
                      <Textarea data-testid={`content-${key}`} value={content[key] ?? ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className="mt-1.5" />
                    ) : (
                      <Input data-testid={`content-${key}`} value={content[key] ?? ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className="mt-1.5" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="save-content-button" onClick={saveContent} disabled={busy} className="mt-6 rounded-full bg-cocoa text-white hover:bg-cocoadark"><Save className="mr-1.5 h-4 w-4" /> Save all text</Button>
      </section>
    </div>
  );
};
